import './EmblaCarousel.scss';

import { ButtonV2 } from '@travelwin/core';
import { EmblaCarouselType } from 'embla-carousel';
import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from 'react';

import PaginationArrowLeft from './PaginationArrowLeft.svg';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const PaginationButton = ({
  badgeImage,
  onClick,
  altText,
  direction,
}: {
  badgeImage: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  altText: string;
  direction: 'left' | 'right';
}) => {
  const handleKeyPress = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' && onClick) {
      const syntheticEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      event.currentTarget.dispatchEvent(syntheticEvent);
    }
  };

  return (
    <ButtonV2
      variant="link"
      onKeyDown={handleKeyPress}
      onClick={onClick}
      style={{
        transform: direction === 'right' ? 'rotate(180deg)' : 'none',
        width: '50px',
      }}
    >
      <img
        src={badgeImage}
        alt={altText}
        style={{
          width: '50px',
          height: '50px',
        }}
      />
    </ButtonV2>
  );
};

PaginationButton.displayName = 'PaginationButton';

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type PropType = ComponentPropsWithRef<'button'>;

export const PrevButton: React.FC<PropType> = (props) => {
  const { ...restProps } = props;

  return (
    <PaginationButton
      badgeImage={PaginationArrowLeft}
      {...restProps}
      altText={'Move to previous'}
      direction="left"
    />
  );
};

export const NextButton: React.FC<PropType> = (props) => {
  const { ...restProps } = props;

  return (
    <PaginationButton
      {...restProps}
      badgeImage={PaginationArrowLeft}
      altText={'Move to previous'}
      direction="right"
    />
  );
};
